import { useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSecurityMonitoring } from '@/hooks/useSecurityMonitoring';
import { useNotifications } from '@/hooks/useNotifications';

export const useEnhancedSecurity = () => {
  const { logSecurityEvent } = useSecurityMonitoring();
  const { error: showError, warning } = useNotifications();

  // Enhanced company access validation
  const validateCompanyAccess = useCallback(async (
    companyId: string,
    requiredRole: 'admin' | 'member' = 'member'
  ): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('validate_company_access', {
        p_user_id: (await supabase.auth.getUser()).data.user?.id,
        p_company_id: companyId,
        p_required_role: requiredRole
      });

      if (error) {
        console.error('Company access validation failed:', error);
        return false;
      }

      return data === true;
    } catch (err) {
      console.error('Company access validation error:', err);
      return false;
    }
  }, []);

  // Enhanced financial data access validation
  const validateFinancialAccess = useCallback(async (
    tableName: string,
    companyId: string,
    operation: 'read' | 'create' | 'update' | 'delete' = 'read'
  ): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('validate_financial_data_access', {
        p_table_name: tableName,
        p_company_id: companyId,
        p_operation: operation
      });

      if (error) {
        showError({
          title: 'Accès refusé',
          description: 'Vous n\'avez pas les permissions nécessaires pour cette opération financière.'
        });
        return false;
      }

      return data === true;
    } catch (err) {
      console.error('Financial access validation error:', err);
      return false;
    }
  }, [showError]);

  // Session security validation
  const validateSessionSecurity = useCallback(async (): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('validate_session_security');

      if (error) {
        console.error('Session validation failed:', error);
        warning({
          title: 'Session expirée',
          description: 'Votre session pourrait avoir expiré. Veuillez vous reconnecter si nécessaire.'
        });
        return false;
      }

      return data === true;
    } catch (err) {
      console.error('Session validation error:', err);
      return false;
    }
  }, [warning]);

  // Enhanced input validation
  const validateInputSecurity = useCallback(async (
    input: string,
    inputType: string,
    maxLength: number = 1000
  ): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('validate_input_security', {
        p_input: input,
        p_input_type: inputType,
        p_max_length: maxLength
      });

      if (error || !data) {
        showError({
          title: 'Données invalides',
          description: 'Les données saisies contiennent des caractères non autorisés.'
        });
        return false;
      }

      return data === true;
    } catch (err) {
      console.error('Input validation error:', err);
      return false;
    }
  }, [showError]);

  // Enhanced audit logging
  const createAuditLog = useCallback(async (
    operation: string,
    tableName: string,
    recordId?: string,
    metadata?: Record<string, any>
  ): Promise<void> => {
    try {
      await supabase.rpc('enhanced_audit_log', {
        p_operation: operation,
        p_table_name: tableName,
        p_record_id: recordId,
        p_metadata: metadata
      });
    } catch (err) {
      console.error('Audit logging failed:', err);
    }
  }, []);

  // Auto-validate session on mount and periodically
  useEffect(() => {
    validateSessionSecurity();
    
    const interval = setInterval(() => {
      validateSessionSecurity();
    }, 30 * 60 * 1000); // Check every 30 minutes

    return () => clearInterval(interval);
  }, [validateSessionSecurity]);

  return {
    validateCompanyAccess,
    validateFinancialAccess,
    validateSessionSecurity,
    validateInputSecurity,
    createAuditLog
  };
};